import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import config from '../../../../../config/config';
import Fetch from '../../../../../util/core/Fetch';
import { useReportAvailablePeriod } from '../../../Common/contexts/AvailablePeriodContext';

export const useChartsData = (queryConfig, filters, isYearComparison = false, ignoreCnes = false) => {
    const municipalityCode = filters?.cidade
        ? String(filters.cidade).replace(/\D/g, '').slice(0, -1)
        : null;

    const query = useQuery({
        queryKey: [
            queryConfig.key,
            isYearComparison ? null : filters?.ano,
            ignoreCnes ? null : filters?.cnes,
            municipalityCode,
            filters?.mes
        ],
        queryFn: async ({ signal }) => {
            return await Fetch.post(
                config.endpoints.backend + queryConfig.url,
                {
                    filters: {
                        ...(isYearComparison ? {} : { ano: filters?.ano }),
                        ...(ignoreCnes ? {} : { cnes: filters?.cnes }),
                        ...(municipalityCode ? { munic_res: municipalityCode } : {}),
                        mes: filters?.mes
                    }
                },
                signal
            );
        },
        enabled: ignoreCnes || !!filters?.cnes || !!municipalityCode,
        staleTime: 1000 * 60 * 60
    });

    // Alimenta o período disponível com as competências que o backend devolveu.
    // O escopo aqui é a unidade (e a cidade, quando a consulta a usa): trocar
    // qualquer um deles descarta as competências acumuladas do escopo anterior.
    const reportPeriod = useReportAvailablePeriod();
    const scope = `${filters?.cnes || ''}|${municipalityCode || ''}`;

    useEffect(() => {
        if (query.isSuccess) {
            reportPeriod(query.data, scope);
        }
    }, [query.isSuccess, query.data, scope, reportPeriod]);

    return query;
};

const PRODUCTION_TEMPORAL_EVOLUTION = 'production-temporal-evolution-ciha';
const PRODUCTION_BY_MONTH = 'production-by-month-ciha';
const PRODUCTION_MONTHLY_SERIES = 'production-monthly-series-ciha';
const PRODUCTION_PERMANENCE_DISTRIBUTION = 'production-permanence-distribution-ciha';
const PRODUCTION_AVG_PERMANENCE = 'production-avg-permanence-ciha';
const PRODUCTION_KPI = 'production-kpi-ciha';

const PATIENT_BY_SEX = 'patient-by-sex-ciha';
const PATIENT_AGE_PYRAMID = 'patient-age-pyramid-ciha';
const PATIENT_SEX_BY_YEAR = 'patient-sex-by-year-ciha';
const PATIENT_AVG_PERMANENCE_BY_AGE = 'patient-avg-permanence-by-age-ciha';

const PROCEDURES_TOP = 'procedures-top-ciha';
const PROCEDURES_BY_MODALITY = 'procedures-by-modality-ciha';
const PROCEDURES_MODALITY_BY_YEAR = 'procedures-modality-by-year-ciha';
const PROCEDURES_UTI_DISTRIBUTION = 'procedures-uti-distribution-ciha';

const FINANCING_BY_SOURCE = 'financing-by-source-ciha';
const FINANCING_SOURCE_BY_YEAR = 'financing-source-by-year-ciha';
const FINANCING_BY_CHARGING_REASON = 'financing-by-charging-reason-ciha';
const FINANCING_NATURE_BY_SOURCE = 'financing-nature-by-source-ciha';

const LOCATION_TOP_CNES = 'location-top-cnes-ciha';
const LOCATION_MANAGEMENT_BY_YEAR = 'location-management-by-year-ciha';
const LOCATION_BY_NATURE = 'location-by-nature-ciha';
const LOCATION_TOP_MUNICIPALITIES = 'location-top-municipalities-ciha';

const EPIDEMIOLOGY_TOP_MORTALITY_CIDS = 'epidemiology-top-mortality-cids-ciha';
const EPIDEMIOLOGY_MORTALITY_EVOLUTION = 'epidemiology-mortality-evolution-ciha';
const EPIDEMIOLOGY_BY_CID_CHAPTER = 'epidemiology-by-cid-chapter-ciha';
const EPIDEMIOLOGY_TOP_DIAGNOSIS = 'epidemiology-top-diagnosis-ciha';

export const QUERY_KEYS_CIHA = {
    PRODUCTION_TEMPORAL_EVOLUTION,
    PRODUCTION_BY_MONTH,
    PRODUCTION_MONTHLY_SERIES,
    PRODUCTION_PERMANENCE_DISTRIBUTION,
    PRODUCTION_AVG_PERMANENCE,
    PRODUCTION_KPI,
    PATIENT_BY_SEX,
    PATIENT_AGE_PYRAMID,
    PATIENT_SEX_BY_YEAR,
    PATIENT_AVG_PERMANENCE_BY_AGE,
    PROCEDURES_TOP,
    PROCEDURES_BY_MODALITY,
    PROCEDURES_MODALITY_BY_YEAR,
    PROCEDURES_UTI_DISTRIBUTION,
    FINANCING_BY_SOURCE,
    FINANCING_SOURCE_BY_YEAR,
    FINANCING_BY_CHARGING_REASON,
    FINANCING_NATURE_BY_SOURCE,
    LOCATION_TOP_CNES,
    LOCATION_MANAGEMENT_BY_YEAR,
    LOCATION_BY_NATURE,
    LOCATION_TOP_MUNICIPALITIES,
    EPIDEMIOLOGY_TOP_MORTALITY_CIDS,
    EPIDEMIOLOGY_MORTALITY_EVOLUTION,
    EPIDEMIOLOGY_BY_CID_CHAPTER,
    EPIDEMIOLOGY_TOP_DIAGNOSIS
};
